import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { opsFetch } from "./client.js";

export type ContractorStatus = "LEAD" | "PENDING_REVIEW" | "APPROVED" | "SUSPENDED";

export interface AuditEntry {
  at: string;
  by: string;
  action: string;
  note?: string;
}

export interface ContractorRecord {
  contractorId: string;
  status: ContractorStatus;
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  website?: string;
  serviceCategories: string[];
  /** The pro's own words for what they do — searched, and worth showing. */
  services?: string[];
  serviceArea: { postalCodes: string[]; cities: string[] };
  needsTriage?: string;
  yearsExperience: number;
  licenseInfo?: string;
  insuranceInfo?: string;
  about?: string;
  emailSuppressed: boolean;
  source: "intake" | "manual" | "portal";
  audit: AuditEntry[];
  createdAt: string;
  updatedAt: string;
}

export interface InvitationSummary {
  invitationId: string;
  rfqId: string;
  status: string;
  createdAt: string;
}

/** Human labels for the shared service-category enum (internal display). */
export const categoryLabels: Record<string, string> = {
  plumbing: "Plumbing",
  electrical: "Electrical",
  hvac: "HVAC",
  roofing: "Roofing",
  landscaping: "Landscaping",
  painting: "Painting",
  cleaning: "Cleaning",
  appliance_repair: "Appliance repair",
  general_handyman: "General handyman",
  pest_control: "Pest control",
  other: "General services",
};

export function useContractors(status?: string) {
  return useQuery({
    queryKey: ["ops", "contractors", status ?? "(default)"],
    queryFn: () =>
      opsFetch<{ items: ContractorRecord[] }>(
        `/ops/contractors${status ? `?status=${encodeURIComponent(status)}` : ""}`,
      ),
  });
}

/**
 * Shortest term the picker will send. Mirrors MIN_SEARCH_TERM on the
 * contractor side, which enforces the same floor — this one only spares the
 * round trip.
 */
export const MIN_SEARCH_LENGTH = 2;

/**
 * Registry-wide search, used by the RFQ page's "invite any pro" picker.
 *
 * Deliberately not the matching query: matching only ever answers "who covers
 * this category in this ZIP", and this exists for the choices it can't make.
 * The server searches business name, contact, email, phone, categories and
 * service area across every status, and reports `truncated` when a term was
 * too broad to return whole.
 *
 * Two characters minimum — a single letter matches most of the registry and
 * is never what an operator meant.
 *
 * Deliberately NO `placeholderData`: keeping the previous term's rows on
 * screen while the next term loads would leave live Invite buttons sitting
 * under a heading that names a different search. The caller debounces, which
 * is what actually stops the list flickering.
 */
export function useContractorSearch(term: string) {
  const q = term.trim();
  return useQuery({
    queryKey: ["ops", "contractors", "search", q],
    enabled: q.length >= MIN_SEARCH_LENGTH,
    queryFn: () =>
      opsFetch<{ items: ContractorRecord[]; truncated?: boolean }>(
        `/ops/contractors?q=${encodeURIComponent(q)}`,
      ),
  });
}

export function useContractor(contractorId: string | undefined) {
  return useQuery({
    queryKey: ["ops", "contractor", contractorId],
    enabled: Boolean(contractorId),
    queryFn: () =>
      opsFetch<{ contractor: ContractorRecord; invitations: InvitationSummary[] }>(
        `/ops/contractors/${contractorId!}`,
      ),
  });
}

export interface ApproveBody {
  serviceCategories: string[];
  serviceArea: { postalCodes: string[]; cities: string[] };
  note?: string;
}

/** Lifecycle action (admin only on the server). `by` is stamped server-side. */
export function useContractorAction(contractorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      action: "approve" | "suspend" | "reinstate";
      body?: ApproveBody | { note?: string };
    }) =>
      opsFetch(`/ops/contractors/${contractorId}/${input.action}`, {
        method: "POST",
        json: input.body ?? {},
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ops", "contractor", contractorId] });
      void qc.invalidateQueries({ queryKey: ["ops", "contractors"] });
    },
  });
}
